/**
 * MetadataStore - MobX store for managing authors and teams metadata
 *
 * This store:
 * - Loads authors.json and teams.json from /data directory
 * - Provides access to author and team mappings
 * - Can be initialized independently or as part of the overall data loading
 */

import { makeAutoObservable, runInAction } from "mobx"
import { fetchJson } from "../lib/fetch"
import { logger } from "../lib/logger"
import type { AuthorsMap, TeamsMap } from "../types/data"

export class MetadataStore {
  // Loading state
  isLoading = false
  loadError: string | null = null

  // Metadata
  authors: AuthorsMap = {}
  teams: TeamsMap = {}

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize the store by loading metadata files
   */
  async initialize(): Promise<void> {
    if (this.isLoading) {
      logger.debug("MetadataStore already initializing, skipping", {
        component: "MetadataStore",
      })
      return
    }

    try {
      runInAction(() => {
        this.isLoading = true
        this.loadError = null
      })

      logger.info("Initializing MetadataStore...", {
        component: "MetadataStore",
      })
      logger.track("metadata_store_init_started")

      // Load both metadata files in parallel
      const [authors, teams] = await Promise.all([
        fetchJson<AuthorsMap>("/data/authors.json").catch((error) => {
          logger.warn("Failed to load authors.json, using empty map", {
            component: "MetadataStore",
            error,
          })
          return {} as AuthorsMap
        }),
        fetchJson<TeamsMap>("/data/teams.json").catch((error) => {
          logger.warn("Failed to load teams.json, using empty map", {
            component: "MetadataStore",
            error,
          })
          return {} as TeamsMap
        }),
      ])

      runInAction(() => {
        this.authors = authors
        this.teams = teams
      })

      logger.info("MetadataStore initialized successfully", {
        component: "MetadataStore",
        authorsCount: Object.keys(authors).length,
        teamsCount: Object.keys(teams).length,
      })
      logger.track("metadata_store_init_success", {
        authorsCount: Object.keys(authors).length,
        teamsCount: Object.keys(teams).length,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize MetadataStore"

      runInAction(() => {
        this.loadError = errorMessage
      })

      logger.error("Failed to initialize MetadataStore", error, {
        component: "MetadataStore",
      })
      logger.track("metadata_store_init_failed", { error: errorMessage })

      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Get author handle by ID
   */
  getAuthor(authorId: string): string {
    return this.authors[authorId] || authorId
  }

  /**
   * Get team by author ID
   */
  getTeam(authorId: string): string | undefined {
    return this.teams[authorId]
  }

  /**
   * Get all authors as an array
   */
  get allAuthors(): Array<{ id: string; handle: string }> {
    return Object.entries(this.authors).map(([id, handle]) => ({ id, handle }))
  }

  /**
   * Get all teams as an array
   */
  get allTeams(): Array<{ authorId: string; team: string }> {
    return Object.entries(this.teams).map(([authorId, team]) => ({
      authorId,
      team,
    }))
  }

  /**
   * Get unique team names
   */
  get uniqueTeams(): string[] {
    const teams = new Set(Object.values(this.teams))
    return Array.from(teams).sort()
  }

  /**
   * Get authors by team
   */
  getAuthorsByTeam(team: string): Array<{ id: string; handle: string }> {
    const authorIds = Object.entries(this.teams)
      .filter(([, t]) => t === team)
      .map(([id]) => id)

    return authorIds.map((id) => ({
      id,
      handle: this.getAuthor(id),
    }))
  }

  /**
   * Check if metadata is loaded
   */
  get isLoaded(): boolean {
    return (
      !this.isLoading &&
      this.loadError === null &&
      (Object.keys(this.authors).length > 0 || Object.keys(this.teams).length > 0)
    )
  }

  /**
   * Reset the store
   */
  reset(): void {
    this.isLoading = false
    this.loadError = null
    this.authors = {}
    this.teams = {}
    logger.info("MetadataStore reset", { component: "MetadataStore" })
  }
}
